import type {
  AiDataCategory,
  AiDocSegment,
  AiDocumentSpec,
  AiShareOption,
  AiWorkspaceOption,
} from "../../scenario/types";

/**
 * The learner's choices versus the safe choices, for the end-of-episode
 * report. Everything here is DERIVED from data the component already scores
 * with (`category`, `neededForTask`, `classification`, `sufficientForTask`),
 * so the "safe choice" shown on the report can never disagree with the grade:
 *
 *   - a line is "sensitive" (should be blacked out) when it has a hidden
 *     category and is NOT needed for the task -- exactly the lines
 *     `evaluate()` in AiAssistant.tsx counts as unnecessary disclosures;
 *   - a "Strictly confidential" file raises the tier to "high" the moment it is
 *     attached, even redacted, so the only safe choice for it is to leave it out;
 *   - a file with sensitive lines is safe only when every one of them is
 *     blacked out first (or when it is left out and a clean file covers the task).
 */

/** What the learner picked in the file panel. "no" = "redact it first". */
export type FileDecision = "yes" | "no" | "skip";
/** What actually happened to the file. */
export type FileAction = "yes" | "redact" | "skip";
export type ShareMethod = AiShareOption["kind"];
/** match = the safe choice, ok = also acceptable, differs = not safe. */
export type Verdict = "match" | "ok" | "differs";

export function isSensitive(seg: AiDocSegment): boolean {
  return !!seg.category && !seg.neededForTask;
}

export function sensitiveSegments(doc: AiDocumentSpec): AiDocSegment[] {
  return doc.segments.filter(isSensitive);
}

const ACTION_LABEL: Record<FileAction, string> = {
  yes: "Sent as-is",
  redact: "Redacted copy",
  skip: "Left out",
};
export function actionLabel(action: FileAction, hiddenCount = 0): string {
  if (action === "redact") return `Redacted copy (${hiddenCount} line${hiddenCount === 1 ? "" : "s"} blacked out)`;
  return ACTION_LABEL[action];
}

/** Plain-language reason each hidden category should not leave the company. */
export const CATEGORY_WHY: Record<AiDataCategory, string> = {
  personal: "A named person's contact details are not needed for a summary.",
  customer_identity: "The customer's identity is not needed to summarise the terms.",
  commercial: "Confidential commercial detail that the request did not ask for.",
  financial: "Bank and payment details are never needed for a summary.",
  credential: "An access code works like a password. It must never go into an AI tool.",
  internal_project: "An internal codename identifies the project and is not needed.",
  signature: "Signatures identify people and are not needed for a summary.",
  metadata: "Internal comments and review notes are not part of the contract itself.",
};

export interface SafeChoice {
  best: FileAction;
  alsoOk: FileAction[];
  /** short label of the best choice, e.g. "Redact it first". */
  label: string;
  /** one line about the other acceptable choice, if any. */
  alt: string | null;
  why: string;
}

export function safeChoiceFor(doc: AiDocumentSpec, docs: AiDocumentSpec[]): SafeChoice {
  const strict = doc.classification === "Strictly confidential";
  const sensitive = sensitiveSegments(doc);
  const cleanSufficientElsewhere = docs.some(
    (d) =>
      d.id !== doc.id &&
      d.classification !== "Strictly confidential" &&
      !!d.sufficientForTask &&
      sensitiveSegments(d).length === 0
  );

  if (strict) {
    return {
      best: "skip",
      alsoOk: [],
      label: "Do not attach it",
      alt: null,
      why: "It was never asked for, and it holds negotiation tactics and board opinions that must stay inside the company. Attaching it at all is a serious leak, even with lines blacked out.",
    };
  }
  if (sensitive.length > 0) {
    return {
      best: "redact",
      alsoOk: cleanSufficientElsewhere ? ["skip"] : [],
      label: "Redact it first",
      alt: cleanSufficientElsewhere ? "Leaving it out is also safe, because another file already covers the task." : null,
      why: "The summary needs a few clauses from it, but it also holds personal, financial and secret lines. Black those out first, or leave it out and use the trimmed clauses instead.",
    };
  }
  if (doc.sufficientForTask) {
    return {
      best: "yes",
      alsoOk: [],
      label: "Send it as-is",
      alt: null,
      why: "It holds only what was asked for and nothing secret, so it is safe to send as it is.",
    };
  }
  return {
    best: "yes",
    alsoOk: ["skip"],
    label: "Send it as-is",
    alt: "Leaving it out is also fine, because it cannot finish the summary on its own.",
    why: "It is already public, so sending it cannot leak anything. It just does not hold the contract terms.",
  };
}

export type LineStatus = "correct" | "missed" | "over_needed" | "over_harmless";

export interface LineCheck {
  seg: AiDocSegment;
  /** this line should have been blacked out. */
  shouldHide: boolean;
  /** the learner blacked it out. */
  didHide: boolean;
  status: LineStatus;
}

export interface FileReviewRow {
  doc: AiDocumentSpec;
  action: FileAction;
  hiddenCount: number;
  safe: SafeChoice;
  verdict: Verdict;
  note: string;
  /** every line that should be blacked out, plus any extra line the learner hid. */
  lines: LineCheck[];
  /** counts over `lines` / over the sensitive lines of the file. */
  sensitiveTotal: number;
  correct: number;
  missed: number;
  overNeeded: number;
  overHarmless: number;
  /** sensitive lines that went out readable (attached files only). */
  sentReadable: number;
}

export interface ReviewData {
  account: {
    chosen: AiWorkspaceOption;
    safeLabel: string;
    verdict: Verdict;
    note: string;
  };
  files: FileReviewRow[];
  share: {
    method: ShareMethod;
    safeLabel: string;
    verdict: Verdict;
    note: string;
  } | null;
  /** how many of the decisions were safe (match or ok), and how many there were. */
  safeCount: number;
  total: number;
}

const SHARED_LOGIN_NOTE = "A shared team login is risky too: anyone who has it can read the whole chat history, including attached files.";
const WORKSPACE_NOTE: Record<AiWorkspaceOption["kind"], string> = {
  corporate: `The company's managed workspace: access, logging and retention are under the company's control. ${SHARED_LOGIN_NOTE}`,
  personal: `The company does not manage a personal account. What you put in it can be kept and reused outside the company, and the company cannot see, audit or remove it. ${SHARED_LOGIN_NOTE}`,
  shared: "A shared login lets anyone with it read the whole chat later, including the files you attached.",
};

export function buildReview(args: {
  docs: AiDocumentSpec[];
  attached: string[];
  redactions: Record<string, Set<string>>;
  workspace: AiWorkspaceOption;
  workspaces: AiWorkspaceOption[];
  shareMethod: ShareMethod | "none";
  appName: string;
}): ReviewData {
  const { docs, attached, redactions, workspace, workspaces, shareMethod, appName } = args;
  const attachedSet = new Set(attached);
  const hiddenOf = (id: string): Set<string> => redactions[id] ?? new Set<string>();

  // Another attached file (other than `excludeId`) that is clean enough to
  // finish the task on its own: not strictly confidential, flagged sufficient,
  // and with every sensitive line blacked out.
  const safeSufficientAttached = (excludeId: string): boolean =>
    docs.some((d) => {
      if (d.id === excludeId || !attachedSet.has(d.id)) return false;
      if (!d.sufficientForTask || d.classification === "Strictly confidential") return false;
      const hidden = hiddenOf(d.id);
      return sensitiveSegments(d).every((s) => hidden.has(s.id));
    });

  const files: FileReviewRow[] = docs.map((doc) => {
    const isAttached = attachedSet.has(doc.id);
    const hidden = hiddenOf(doc.id);
    const action: FileAction = !isAttached ? "skip" : hidden.size > 0 ? "redact" : "yes";
    const safe = safeChoiceFor(doc, docs);
    const sens = sensitiveSegments(doc);

    const lines: LineCheck[] = [];
    let correct = 0;
    let missed = 0;
    let overNeeded = 0;
    let overHarmless = 0;
    for (const seg of doc.segments) {
      const shouldHide = isSensitive(seg);
      const didHide = hidden.has(seg.id);
      if (!shouldHide && !didHide) continue;
      let status: LineStatus;
      if (shouldHide) status = didHide ? "correct" : "missed";
      else status = seg.neededForTask ? "over_needed" : "over_harmless";
      if (status === "correct") correct += 1;
      else if (status === "missed") missed += 1;
      else if (status === "over_needed") overNeeded += 1;
      else overHarmless += 1;
      lines.push({ seg, shouldHide, didHide, status });
    }
    const sentReadable = isAttached ? sens.filter((s) => !hidden.has(s.id)).length : 0;

    let verdict: Verdict;
    let note: string;
    if (safe.best === "skip") {
      if (action === "skip") {
        verdict = "match";
        note = "Left out. It was never asked for and it does not belong in an AI tool.";
      } else {
        verdict = "differs";
        note =
          action === "redact"
            ? `You attached it with ${hidden.size} line${hidden.size === 1 ? "" : "s"} blacked out. Notes like these should not leave the company, even redacted.`
            : "You sent it as it is. It holds negotiation tactics and opinions that the summary never needed.";
      }
    } else if (safe.best === "redact") {
      if (action === "redact") {
        if (missed > 0) {
          verdict = "differs";
          note = `${missed} of ${sens.length} sensitive line${sens.length === 1 ? " was" : "s were"} still readable when you sent it.`;
        } else if (overNeeded > 0) {
          verdict = "ok";
          note = `Every sensitive line was blacked out, but you also hid ${overNeeded} line${
            overNeeded === 1 ? "" : "s"
          } the summary needs.`;
        } else {
          verdict = "match";
          note = `All ${sens.length} sensitive lines were blacked out and the lines the task needs stayed readable.`;
        }
      } else if (action === "yes") {
        verdict = "differs";
        note = `Sent as it is: ${sens.length} sensitive line${sens.length === 1 ? "" : "s"} (personal, financial or secret details) went to ${appName}.`;
      } else if (safe.alsoOk.includes("skip") && safeSufficientAttached(doc.id)) {
        verdict = "ok";
        note = "Left out. Another file you attached already covers the task, so nothing sensitive left.";
      } else {
        verdict = "differs";
        note = "Left out, but nothing else you attached could finish the summary.";
      }
    } else {
      // best === "yes": a file with nothing sensitive in it.
      if (action === "yes") {
        verdict = "match";
        note = doc.sufficientForTask
          ? "Sent as it is. It holds only what was asked for."
          : "Sent as it is. It is public, so nothing leaked.";
      } else if (action === "redact") {
        verdict = "ok";
        note =
          overNeeded > 0
            ? "Safe, but you hid lines the summary needs, which leaves a gap in the answer."
            : "Nothing in this file needed hiding, so blacking lines out was not necessary.";
      } else if (doc.sufficientForTask) {
        if (safeSufficientAttached(doc.id)) {
          verdict = "ok";
          note = "Left out. Another safe file you attached covers the task.";
        } else {
          verdict = "differs";
          note = "Left out, but this was the safe file that could complete the summary.";
        }
      } else {
        verdict = "ok";
        note = "Left out. It is public, but it could not have finished the summary anyway.";
      }
    }

    return {
      doc,
      action,
      hiddenCount: hidden.size,
      safe,
      verdict,
      note,
      lines,
      sensitiveTotal: sens.length,
      correct,
      missed,
      overNeeded,
      overHarmless,
      sentReadable,
    };
  });

  const corporate = workspaces.find((w) => w.kind === "corporate");
  const account: ReviewData["account"] = {
    chosen: workspace,
    safeLabel: corporate?.label ?? "The company's managed workspace",
    verdict: workspace.kind === "corporate" ? "match" : "differs",
    note: WORKSPACE_NOTE[workspace.kind],
  };

  let share: ReviewData["share"] = null;
  if (shareMethod !== "none") {
    const safeLabel = "Paste the summary text into an internal reply";
    if (shareMethod === "public_link") {
      share = {
        method: shareMethod,
        safeLabel,
        verdict: "differs",
        note: "A public link opens the whole chat and every attached file to anyone who gets the URL, with no sign-in and no way to see who opened it.",
      };
    } else if (shareMethod === "guidance") {
      share = {
        method: shareMethod,
        safeLabel,
        verdict: "ok",
        note: "You paused to ask before sharing. Slower, but nothing else could leave.",
      };
    } else {
      share = {
        method: shareMethod,
        safeLabel,
        verdict: "match",
        note: "Only the words you chose left the chat. The conversation and the files stayed behind.",
      };
    }
  }

  const all: Verdict[] = [account.verdict, ...files.map((f) => f.verdict), ...(share ? [share.verdict] : [])];
  return {
    account,
    files,
    share,
    safeCount: all.filter((v) => v !== "differs").length,
    total: all.length,
  };
}
